/**
 * Input Method System - Core Types and Logic
 * Single source of truth for all input method behavior
 */

// Input Groups - Categories of input methods
export const INPUT_GROUPS = {
  POINTER: 'POINTER',   // Mouse, Touch, External Tobii
  GAZE: 'GAZE',         // Webcam MediaPipe (gaze + head tracking)
  SCANNING: 'SCANNING', // Switches, EyeControl Device, Mouth Gestures
  VOICE: 'VOICE'        // Voice Commands
} as const;

export type InputGroup = typeof INPUT_GROUPS[keyof typeof INPUT_GROUPS];

// Scan Modes for SCANNING group
export const SCAN_MODES = {
  NONE: 'NONE',
  AUTO: 'AUTO_SCAN',           // Timer-based auto scanning
  STEP: 'STEP_SCAN',           // Manual: Next/Prev/Select
  ADVANCED: 'ADVANCED_STEP',   // Manual: Next/Prev/Select/Back
  SPATIAL: 'SPATIAL'           // Directional: Up/Down/Left/Right/Select
} as const;

export type ScanMode = typeof SCAN_MODES[keyof typeof SCAN_MODES];

// Input Method Identifiers
export const InputMethod = {
  STANDARD_POINTER: 'standard_pointer',
  GAZE_TRACKING: 'gaze_tracking',
  HEAD_TRACKING: 'head_tracking',
  VOICE_CONTROL: 'voice_control',
  EYE_CONTROL: 'eye_control',
  EXTERNAL_SWITCH: 'external_switch',
  MOUTH_GESTURES: 'mouth_gestures'
} as const;

export type InputMethodType = typeof InputMethod[keyof typeof InputMethod];

// Voice Control States
export const VoiceState = {
  STANDBY: 'standby',       // Waiting for wake word
  LISTENING: 'listening',   // Actively listening for commands
  PROCESSING: 'processing', // Processing a command
  SLEEPING: 'sleeping'      // Microphone off
} as const;

export type VoiceStateType = typeof VoiceState[keyof typeof VoiceState];

// Input Behavior Configuration
export interface InputBehavior {
  group: InputGroup;
  cursor: 'system' | 'virtual' | 'both';
  scanning: ScanMode;
  dwell: boolean;
  showFocusRing: boolean;
  showNumberTags: boolean;
  showVirtualCursor: boolean;
  hideCursor: boolean;
}

/**
 * Resolve input behavior based on method and input count
 * This is the Single Source of Truth for the entire app's behavior
 */
export function resolveInputBehavior(methodId: string, inputCount: number = 1): InputBehavior {
  
  // 1. POINTER METHODS (System Cursor, Direct Click)
  if (['touch', 'mouse', 'standard_pointer', 'external'].includes(methodId)) {
    return {
      group: INPUT_GROUPS.POINTER,
      cursor: 'system',
      scanning: SCAN_MODES.NONE,
      dwell: false,
      showFocusRing: false,
      showNumberTags: false,
      showVirtualCursor: false,
      hideCursor: false
    };
  }

  // 2. GAZE TRACKING (Virtual Cursor + Dwell Click)
  if (['gaze_tracking', 'webcam'].includes(methodId)) {
    return {
      group: INPUT_GROUPS.GAZE,
      cursor: 'both',
      scanning: SCAN_MODES.NONE,
      dwell: true,
      showFocusRing: false,
      showNumberTags: false,
      showVirtualCursor: true,
      hideCursor: false
    };
  }

  // 3. HEAD TRACKING (Virtual Cursor + Dwell Click)
  if (['head_tracking', 'headtracking'].includes(methodId)) {
    return {
      group: INPUT_GROUPS.GAZE,
      cursor: 'both',
      scanning: SCAN_MODES.NONE,
      dwell: true,
      showFocusRing: false,
      showNumberTags: false,
      showVirtualCursor: true,
      hideCursor: false
    };
  }

  // 4. VOICE CONTROL (Number Tags)
  if (['voice', 'voice_control'].includes(methodId)) {
    return {
      group: INPUT_GROUPS.VOICE,
      cursor: 'system',
      scanning: SCAN_MODES.NONE,
      dwell: false,
      showFocusRing: false,
      showNumberTags: true,
      showVirtualCursor: false,
      hideCursor: false
    };
  }

  // 5. EYE CONTROL DEVICE (Scanning based on gesture count)
  if (['eye_control', 'eyecontrol'].includes(methodId)) {
    let scanMode: ScanMode = SCAN_MODES.SPATIAL;
    
    if (inputCount >= 5) {
      scanMode = SCAN_MODES.SPATIAL;       // 5 gestures = directional
    } else if (inputCount >= 4) {
      scanMode = SCAN_MODES.ADVANCED;      // 4 gestures = Next/Prev/Select/Back
    } else if (inputCount >= 3) {
      scanMode = SCAN_MODES.STEP;          // 3 gestures = Next/Prev/Select
    } else if (inputCount >= 1) {
      scanMode = SCAN_MODES.AUTO;          // 1-2 gestures = Auto-scan with timer
    }
    
    return {
      group: INPUT_GROUPS.SCANNING,
      cursor: 'system',
      scanning: scanMode,
      dwell: false,
      showFocusRing: true,
      showNumberTags: false,
      showVirtualCursor: false,
      hideCursor: false
    };
  }

  // 6. EXTERNAL SWITCH (Scanning based on switch count)
  if (['external_switch', 'switch_control'].includes(methodId)) {
    let scanMode: ScanMode = SCAN_MODES.AUTO;
    
    if (inputCount >= 5) {
      scanMode = SCAN_MODES.SPATIAL;       // 5+ switches = directional
    } else if (inputCount >= 4) {
      scanMode = SCAN_MODES.ADVANCED;      // 4 switches = Next/Prev/Select/Back
    } else if (inputCount >= 2) {
      scanMode = SCAN_MODES.STEP;          // 2-3 switches = manual step
    } else {
      scanMode = SCAN_MODES.AUTO;          // 1 switch = auto-scan
    }

    return {
      group: INPUT_GROUPS.SCANNING,
      cursor: 'system',
      scanning: scanMode,
      dwell: false,
      showFocusRing: true,
      showNumberTags: false,
      showVirtualCursor: false,
      hideCursor: false
    };
  }

  // 7. MOUTH GESTURES (Scanning based on configured gesture count)
  if (['mouth_gestures'].includes(methodId)) {
    let scanMode: ScanMode = SCAN_MODES.AUTO;
    
    if (inputCount >= 4) {
      scanMode = SCAN_MODES.ADVANCED;      // 4 gestures = Next/Prev/Select/Back
    } else if (inputCount >= 2) {
      scanMode = SCAN_MODES.STEP;          // 2-3 gestures = manual step
    } else {
      scanMode = SCAN_MODES.AUTO;          // 1 gesture = auto-scan
    }
    
    return {
      group: INPUT_GROUPS.SCANNING,
      cursor: 'system',
      scanning: scanMode,
      dwell: false,
      showFocusRing: true,
      showNumberTags: false,
      showVirtualCursor: false,
      hideCursor: false
    };
  }

  // Default fallback - Standard Pointer
  return {
    group: INPUT_GROUPS.POINTER,
    cursor: 'system',
    scanning: SCAN_MODES.NONE,
    dwell: false,
    showFocusRing: false,
    showNumberTags: false,
    showVirtualCursor: false,
    hideCursor: false
  };
}

// Map control_method values to InputMethod
export const controlMethodToInputMethod: Record<string, InputMethodType> = {
  'touch': InputMethod.STANDARD_POINTER,
  'mouse': InputMethod.STANDARD_POINTER,
  'external': InputMethod.STANDARD_POINTER,
  'standard_pointer': InputMethod.STANDARD_POINTER,
  'switch_control': InputMethod.EXTERNAL_SWITCH,
  'eyecontrol': InputMethod.EYE_CONTROL,
  'eye_control': InputMethod.EYE_CONTROL,
  'external_switch': InputMethod.EXTERNAL_SWITCH,
  'voice': InputMethod.VOICE_CONTROL,
  'voice_control': InputMethod.VOICE_CONTROL,
  'webcam': InputMethod.GAZE_TRACKING,
  'gaze_tracking': InputMethod.GAZE_TRACKING,
  'headtracking': InputMethod.HEAD_TRACKING,
  'head_tracking': InputMethod.HEAD_TRACKING,
  'mouth_gestures': InputMethod.MOUTH_GESTURES
};
